import {
  ITutorialDto,
  ITutorialJourneyProfileDto,
  ITutorialJourneyStepDto,
} from "@growth-machine-llc/stridist-api-client";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import TutorialJourneyProfilesService from "../../../services/TutorialJourneyProfilesService";
import { TUTORIAL_PROFILES_QUERY_KEY } from "./TutorialJourneysList";
import { AppliedProfileChip } from "../AppliedProfileChip";

const useStyles = makeStyles((theme) => ({
  journeyBox: {
    marginBottom: theme.spacing(2),
    border: "1px solid #ccc",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  journeyHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  journeyContent: {
    marginTop: theme.spacing(1),
  },
  stepRow: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  appliedText: {
    color: "green",
  },
  selectAllButton: {
    marginBottom: theme.spacing(1),
  },
}));

interface ProfileUsageDialogProps {
  open: boolean;
  onClose: () => void;
  profile: ITutorialJourneyProfileDto;
  journeys: ITutorialDto[];
}

export const ProfileUsageDialog: React.FC<ProfileUsageDialogProps> = ({
  open,
  onClose,
  profile,
  journeys,
}) => {
  const s = useStyles();
  const queryClient = useQueryClient();

  const [expandedJourneyId, setExpandedJourneyId] = useState<number | null>(
    null,
  );
  const [selectedSteps, setSelectedSteps] = useState<{
    [journeyId: number]: number[];
  }>({});
  const [initialSelectedSteps, setInitialSelectedSteps] = useState<{
    [journeyId: number]: number[];
  }>({});

  useEffect(() => {
    if (open) {
      const initial: { [journeyId: number]: number[] } = {};
      journeys.forEach((journey) => {
        if (journey.id) {
          let ids: number[] = [];
          if (journey.steps) {
            ids =
              journey.steps
                .filter((step) => step.profile?.name === profile.name)
                .map((step) => step.id!) || [];
          }
          if (
            journey.journeyActivator &&
            journey.journeyActivator.profile?.name === profile.name
          ) {
            ids.push(journey.journeyActivator.id!);
          }
          initial[journey.id] = ids;
        }
      });
      setInitialSelectedSteps(initial);
      setSelectedSteps(initial);
    }
  }, [open, journeys, profile]);

  const handleToggleJourney = (journeyId: number) => {
    setExpandedJourneyId((prev) => (prev === journeyId ? null : journeyId));
  };

  const handleStepToggle = (journeyId: number, stepId: number) => {
    setSelectedSteps((prev) => {
      const prevSelected = prev[journeyId] || [];
      return {
        ...prev,
        [journeyId]: prevSelected.includes(stepId)
          ? prevSelected.filter((id) => id !== stepId)
          : [...prevSelected, stepId],
      };
    });
  };

  const handleSelectAllSteps = (journey: ITutorialDto) => {
    if (!journey.id) return;
    let allIds: number[] = [];
    if (journey.steps) {
      allIds = journey.steps.map((step) => step.id!);
    }
    if (journey.journeyActivator) {
      allIds.push(journey.journeyActivator.id!);
    }
    setSelectedSteps((prev) => ({ ...prev, [journey.id!]: allIds }));
  };

  const isDirty = (journeyId: number): boolean => {
    const initial = initialSelectedSteps[journeyId] || [];
    const current = selectedSteps[journeyId] || [];
    return (
      JSON.stringify([...initial].sort()) !==
      JSON.stringify([...current].sort())
    );
  };

  const { mutate: applyProfile, isPending: applyingProfile } = useMutation({
    mutationFn: TutorialJourneyProfilesService.applyTutorialJourneyProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TUTORIAL_PROFILES_QUERY_KEY],
      });
      onClose();
    },
  });

  const handleGlobalApply = () => {
    journeys.forEach((journey) => {
      if (!journey.id) return;
      const selected = selectedSteps[journey.id] || [];
      if (selected.length === 0) return;

      const activatorIds: number[] = [];
      if (
        journey.journeyActivator &&
        selected.includes(journey.journeyActivator.id!) &&
        journey.journeyActivator.profile?.id !== profile.id
      ) {
        activatorIds.push(journey.journeyActivator.id!);
      }

      const stepIds = journey.steps
        ? journey.steps
            .filter(
              (step) =>
                selected.includes(step.id!) &&
                (!journey.journeyActivator ||
                  step.id !== journey.journeyActivator.id),
            )
            .map((step) => step.id!)
        : [];

      applyProfile({
        id: profile.id!,
        journeySteps: stepIds,
        journeyActivatorIds: activatorIds,
      });
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Profile Usage for {profile.name}</DialogTitle>
      <DialogContent dividers>
        {journeys.length === 0 ? (
          <Typography>No journeys found for this profile.</Typography>
        ) : (
          journeys.map((journey) => {
            const journeyId = journey.id!;
            const stepsCount = journey.steps ? journey.steps.length : 0;
            const totalCount = stepsCount + (journey.journeyActivator ? 1 : 0);
            const currentCount = selectedSteps[journeyId]
              ? selectedSteps[journeyId].length
              : 0;
            return (
              <Box key={journey.id} className={s.journeyBox}>
                <Box
                  className={s.journeyHeader}
                  onClick={() => handleToggleJourney(journeyId)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {journey.title}{" "}
                    <Typography variant="caption" color="textSecondary">
                      ({currentCount} / {totalCount} items applied)
                    </Typography>
                  </Typography>
                  {expandedJourneyId === journeyId ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </Box>
                <Collapse
                  in={expandedJourneyId === journeyId}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box className={s.journeyContent} sx={{ ml: 2, mt: 1 }}>
                    {totalCount > 0 ? (
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSelectAllSteps(journey)}
                          className={s.selectAllButton}
                          sx={{ mb: 1 }}
                        >
                          Select All
                        </Button>
                        {journey.steps &&
                          journey.steps.map((step) => {
                            const checked = selectedSteps[journeyId]?.includes(
                              step.id!,
                            );
                            return (
                              <Box
                                key={step.id}
                                className={s.stepRow}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 0.5,
                                }}
                              >
                                <Checkbox
                                  checked={checked}
                                  onChange={() =>
                                    handleStepToggle(journeyId, step.id!)
                                  }
                                />
                                <Typography>Step {step.stepIndex}</Typography>
                                {step.profile && (
                                  <AppliedProfileChip
                                    appliedProfile={step.profile}
                                    isCurrent={step.profile.id === profile.id}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        {journey.journeyActivator && (
                          <Box
                            key={journey.journeyActivator.id}
                            className={s.stepRow}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Checkbox
                              checked={selectedSteps[journeyId]?.includes(
                                journey.journeyActivator.id!,
                              )}
                              onChange={() =>
                                handleStepToggle(
                                  journeyId,
                                  journey.journeyActivator!.id!,
                                )
                              }
                            />
                            <Typography>Activator</Typography>
                            {journey.journeyActivator.profile && (
                              <AppliedProfileChip
                                appliedProfile={
                                  journey.journeyActivator.profile
                                }
                                isCurrent={
                                  journey.journeyActivator.profile.id ===
                                  profile.id
                                }
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2">
                        No steps found in this journey.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGlobalApply}
          disabled={
            applyingProfile ||
            !Object.keys(selectedSteps).some((jid) => isDirty(Number(jid)))
          }
        >
          Apply Profile to All Selected Items
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileUsageDialog;
